// Top-level app: resolves the OIDC session (finishing the redirect callback if
// present), then shows the Login screen or the authenticated Console.
import { useEffect, useState } from "react";
import { completeLoginIfCallback, type User, userManager } from "./auth";
import { Banner } from "./components/Banner";
import { Console } from "./components/Console";
import { Login } from "./components/Login";

type State =
  | { phase: "loading" }
  | { phase: "anon" }
  | { phase: "authed"; user: User }
  | { phase: "error"; message: string };

export function App() {
  const [state, setState] = useState<State>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await completeLoginIfCallback();
        if (cancelled) return;
        if (user && !user.expired) {
          setState({ phase: "authed", user });
        } else {
          setState({ phase: "anon" });
        }
      } catch (e) {
        if (!cancelled) {
          setState({
            phase: "error",
            message: e instanceof Error ? e.message : "Sign-in failed.",
          });
        }
      }
    })();

    // React to token expiry / external sign-out.
    const onUnloaded = () => setState({ phase: "anon" });
    userManager.events.addUserUnloaded(onUnloaded);
    userManager.events.addUserSignedOut(onUnloaded);

    return () => {
      cancelled = true;
      userManager.events.removeUserUnloaded(onUnloaded);
      userManager.events.removeUserSignedOut(onUnloaded);
    };
  }, []);

  switch (state.phase) {
    case "loading":
      return (
        <div className="center-screen">
          <p className="muted">Loading…</p>
        </div>
      );
    case "error":
      return (
        <div className="center-screen">
          <div className="card">
            <Banner kind="error">{state.message}</Banner>
            <Login />
          </div>
        </div>
      );
    case "anon":
      return <Login />;
    case "authed":
      return <Console user={state.user} />;
  }
}
